<template>
  <div class="ToggleSwitch" :class="{ 'ToggleSwitch--twoOptions': hasTwoOptions }">
    <span v-if="leftLabel" :class="['ToggleSwitch-leftLabel', { selected: !model }]">
      {{ leftLabel }}
    </span>

    <SwitchRoot
      v-model="model"
      class="ToggleSwitch-input"
      :aria-label="hasTwoOptions ? (model ? rightLabel : leftLabel) : rightLabel"
    >
      <SwitchThumb class="ToggleSwitch-indicator" />
    </SwitchRoot>

    <span v-if="rightLabel" class="ToggleSwitch-rightLabel" :class="{ selected: model }">
      {{ rightLabel }}
    </span>

    <p v-if="description" class="ToggleSwitch-description">
      {{ description }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { SwitchRoot, SwitchThumb } from 'reka-ui';

const { leftLabel } = defineProps<{
  leftLabel?: string;
  rightLabel?: string;
  description?: string;
}>();

const hasTwoOptions = computed<boolean>(() => !!leftLabel);

const model = defineModel<boolean>({
  default: false,
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {

  @mixin toggle-switch-responsive(
    $mobile: vars.$dimensions-d-8,
    $tablet: vars.$dimensions-d-12,
    $spacing: vars.$dimensions-d-1
  ) {
    --switch-size: #{vars.$dimensions-d-12};
    --switch-indicator-size: calc(var(--switch-size) / 2 - #{$spacing});

    grid-template-columns: [indicator] var(--switch-size) [right] auto;
    &.ToggleSwitch--twoOptions {
      grid-template-columns: [left] auto [indicator] var(--switch-size) [right] auto;
    }

    .ToggleSwitch-input {
      width: var(--switch-size);
    }

    .ToggleSwitch-indicator {
      height: var(--switch-indicator-size);
      width: var(--switch-indicator-size);
      border-radius: vars.$border-radius-full;

      will-change: transform;
    }

    .ToggleSwitch-input[data-state="checked"] .ToggleSwitch-indicator {
      transform: translateX(calc((var(--switch-size) - var(--switch-size) / 2) * var(--transform-direction, 1)));
    }

    @include responsive.mobile {
      --switch-size: #{$mobile};
    }
    @include responsive.tablet {
      --switch-size: #{$tablet};
    }
  }

  .ToggleSwitch {
    $transition-timing: 0.375s;

    display: grid;
    grid-template-rows: repeat(2, auto);
    align-items: center;
    width: max-content;
    cursor: pointer;

    column-gap: vars.$dimensions-d-3;
    row-gap: vars.$dimensions-d-1;

    @include toggle-switch-responsive;

    &-indicator {
      display: flex;
      align-items: center;
      position: relative;
      flex-shrink: 0;
      border-radius: vars.$border-radius-full;
      background-color: vars.$atoms-controls-toggleswitch-off-toggle-circle-color;
      transition: transform $transition-timing ease-in-out;
    }

    &-description {
      white-space: nowrap;
      color: vars.$atoms-controls-toggleswitch-off-highlight-text-color;

      @include text.text-responsive('xs', 'sm', 'md');
    }

    &-leftLabel {
      text-align: end;
      color: vars.$atoms-controls-toggleswitch-off-primary-text-color;
    }

    &-rightLabel {
      color: vars.$atoms-controls-toggleswitch-off-secondary-text-color;
    }

    &-leftLabel, &-rightLabel {
      text-transform: capitalize;
      @include text.text-responsive('sm', 'md', 'lg');
    }

    &-input {
      border: none;
      cursor: pointer;
      outline: none;

      background-color: vars.$atoms-controls-toggleswitch-off-toggle-bg-color;
      border-radius: vars.$atoms-controls-toggleswitch-toggle-border-radius;
      padding: 2px;
      transition: colors $transition-timing ease-in-out;

      &[data-state="checked"] {
        background-color: vars.$atoms-controls-toggleswitch-on-toggle-bg-color;

        .ToggleSwitch-indicator {
          background-color: vars.$atoms-controls-toggleswitch-on-toggle-circle-color;
        }

        &:focus-within {
          outline-color: vars.$color-brand-primary-900;
        }
      }

      &:focus-within {
        outline: 1px solid vars.$color-neutral-gray-500;
        outline-offset: 2px;
      }
    }

    &--twoOptions {
      .ToggleSwitch-description {
        grid-column: indicator;
        justify-self: center;
      }
    }

    // States
    &:has([data-state="checked"]) {
      .ToggleSwitch-description {
        color: vars.$atoms-controls-toggleswitch-on-highlight-text-color;
      }

      .ToggleSwitch-leftLabel {
        color: vars.$atoms-controls-toggleswitch-on-secondary-text-color;
      }

      .ToggleSwitch-rightLabel {
        color: vars.$atoms-controls-toggleswitch-on-primary-text-color;
      }
    }
  }
}
</style>
